import { useSearchParams } from 'react-router-dom'
import { compact } from 'lodash'
import { useMemo, useState } from 'react'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import useAttributionMetrics from 'app/hooks/useAttributionMetrics'
import { AttributionDayType, AttributionRowType, AttributionSummaryType } from 'app/types/attribution'

export type UseAttributionReturn = {
  isV2Enabled: boolean
  fromDate: string
  toDate: string
  locationIds: string[]
  integrationStatus: string
  lifecycleFilter: 'Leads' | 'Qualified Leads' | 'Appointments Scheduled' | 'Patients'
  setLifecycleFilter: (filter: 'Leads' | 'Qualified Leads' | 'Appointments Scheduled' | 'Patients') => void
  summary: AttributionSummaryType | undefined
  days: AttributionDayType[] | undefined
  sourceRows: AttributionRowType[] | undefined
  mediumRows: AttributionRowType[] | undefined
  sourceMediumRows: AttributionRowType[] | undefined
  campaignRows: AttributionRowType[] | undefined
  adCampaignRows: AttributionRowType[] | undefined
  adPlatformRows: AttributionRowType[] | undefined
  channelRows: AttributionRowType[] | undefined
  reportedAttributionRows: AttributionRowType[] | undefined
  isLoading: boolean
  dateChangeHandler: (fromDate: string, toDate: string) => void
}

export function useAttribution(): UseAttributionReturn {
  const isV2Enabled = useFeatureToggle(`attribution-v2`)
  const [searchParams] = useSearchParams()

  const locationIdsMemoed = useMemo(() => searchParams.get(`locationIds`), [searchParams])
  const locationIds = useMemo(() => compact(locationIdsMemoed?.split(`,`) ?? []), [locationIdsMemoed])
  const [fromDate, setFromDate] = useState<string>(searchParams.get(`fromDate`) ?? ``)
  const [toDate, setToDate] = useState<string>(searchParams.get(`toDate`) ?? ``)
  const integrationStatus = useMemo(() => searchParams.get(`integrationStatus`) ?? ``, [searchParams])
  const [lifecycleFilter, setLifecycleFilter] = useState<
    'Leads' | 'Qualified Leads' | 'Appointments Scheduled' | 'Patients'
  >(`Leads`)

  const [
    summary,
    days,
    sourceRows,
    mediumRows,
    sourceMediumRows,
    campaignRows,
    adCampaignRows,
    adPlatformRows,
    channelRows,
    reportedAttributionRows,
    isLoading,
  ] = useAttributionMetrics(fromDate, toDate, locationIds, integrationStatus, isV2Enabled)

  const dateChangeHandler = (fromDate: string, toDate: string) => {
    setFromDate(fromDate)
    setToDate(toDate)
  }

  return {
    isV2Enabled,
    fromDate,
    toDate,
    locationIds,
    integrationStatus,
    lifecycleFilter,
    setLifecycleFilter,
    summary,
    days,
    sourceRows,
    mediumRows,
    sourceMediumRows,
    campaignRows,
    adCampaignRows,
    adPlatformRows,
    channelRows,
    reportedAttributionRows,
    isLoading,
    dateChangeHandler,
  }
}
